import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { SurveyBackground2023 } from '@/components/survey-background-2023';
import data from '@/data/where-you-call-home.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { WhereYouCallHome2023Chapter1 } from '@/components/stories/where-you-call-home/chapter-1';
import { WhereYouCallHome2023Chapter2 } from '@/components/stories/where-you-call-home/chapter-2';
import { WhereYouCallHome2023Chapter3 } from '@/components/stories/where-you-call-home/chapter-3';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as WhereYouCallHomeData }),
  getStoryProps('where-you-call-home', 'global-citizens')
);

export default function WhereYouCallHome(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('where-you-call-home');

  const pageTitle = t({ message: 'Where you call home', id: 'where-you-call-home-2023.title' });
  const pageDescription = t({
    id: 'where-you-call-home-2023.description',
    message:
      'Some people feel most connected to their immediate surroundings. For others, links are further afield.',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.spinach} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="where-you-call-home-2023.theme">Globalization</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="where-you-call-home-2023.tagline">
              <Text type="heading">
                Some feel most <span>connected</span> to their immediate surroundings. For others,
                links are further afield.
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground2023 />

        <WhereYouCallHome2023Chapter1 data={data.younger_identify_as} />
        <WhereYouCallHome2023Chapter2 data={data.younger_identify_as} />
        <WhereYouCallHome2023Chapter3 data={data.younger_identify_with_world} />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            url: sharingUrl,
            title: t({
              id: 'where-you-call-home-2023.sharing.title',
              message: 'Where you call home',
            }),
            text: t({
              id: 'where-you-call-home-2023.sharing.text',
              message:
                'Some people feel most connected to their immediate surroundings. For others, links are further afield.',
            }),
          }}
        >
          <Trans id="where-you-call-home-2023.closing-question">
            What does it mean to be a <AnnotatedType>global citizen?</AnnotatedType>
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
